import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';

import { quickJsActions } from '../../../../quickjs/quickjs.actions';
import { createConfigSelector } from '../../../../state/branding.store';
import { selectRouteParam } from '../../../../state/router';
import { generateUUID } from '../../../../utilities/state.util';
import { InventoryActions } from '../../../job-inventory/job-inventory.actions';
import { jobInventoryFeature } from '../../../job-inventory/job-inventory.feature';
import { InventoryRoom, InventoryItem, InventoryNavStatus } from '../../../job-inventory/job-inventory.state';
import { selectJob } from '../../../job-tool.reducer';

import { NavBaseComponent } from './nav-base';

/** How long a burst of taps is coalesced before the review state is written to the event. */
const REVIEW_PERSIST_DEBOUNCE_MS = 1200;

interface ReviewState {
    /** Inventory weight before this crew touched anything; null until the first store emission. */
    originalWeight: number | null;
    /** itemId → status. Absent means pending. */
    items: Record<string, ItemStatus>;
    /** roomId → the crew approved this room. */
    rooms: Record<string, boolean>;
    scopeConfirmed: boolean;
    /**
     * itemId → what the item held before Confirm Scope zeroed it as missing (**#97**).
     *
     * Kept in the navigator's own state, not on the inventory item, so the office's inventory
     * never carries a half-finished navigator detail. An entry lives until the item is no longer
     * marked missing at a confirm — it deliberately survives "Make changes", because the restore
     * that does is not persisted until the crew confirms again, and a reload in between brings
     * the zeroed item back from the server.
     */
    missingSnapshot: Record<string, MissingSnapshot>;
}

/** An entry of the franchise's inventory catalog (`inventory.items`), as the job page reads it. */
interface CatalogItem {
    reviewerName?: string | null;
    label?: string | null;
    weight?: string | null;
    volume?: string | null;
}

/**
 * An item the crew typed but has not added yet: the weight/volume prompt is open for it (#90).
 * Weight and volume are bound to number inputs, which hand back a number once typed in and an
 * empty string (or null) when cleared; empty means "not given".
 */
interface PendingAdd {
    roomId: string;
    name: string;
    weight: string | number | null;
    volume: string | number | null;
    /** Prefilled from the catalog (both values), or the crew has to estimate. */
    fromCatalog: boolean;
}

interface MissingSnapshot {
    quantity: number;
    /** `added` must survive too — an item the crew added and then marked missing is still theirs. */
    navStatus?: InventoryNavStatus;
}

// Common furniture items for autocomplete suggestions in the add-item field
const COMMON_ITEMS = [
    'Sofa', 'Loveseat', 'Armchair', 'Coffee Table', 'End Table', 'TV Stand',
    'Dining Table', 'Dining Chair', 'Buffet / Sideboard',
    'Queen Bed Frame', 'King Bed Frame', 'Full Bed Frame', 'Twin Bed Frame',
    'Mattress (Queen)', 'Mattress (King)', 'Mattress (Full)', 'Mattress (Twin)',
    'Dresser', 'Nightstand', 'Wardrobe / Armoire', 'Chest of Drawers',
    'Desk', 'Office Chair', 'Bookshelf', 'Filing Cabinet',
    'Washer', 'Dryer', 'Refrigerator', 'Stove / Oven', 'Dishwasher',
    'Microwave', 'TV (Large)', 'TV (Medium)', 'TV (Small)',
    'Mirror (Large)', 'Mirror (Small)',
    'Piano / Keyboard', 'Treadmill / Exercise Machine',
    'Patio Table', 'Patio Chair', 'Outdoor Umbrella',
    'Box (Small)', 'Box (Medium)', 'Box (Large)',
];

type ItemStatus = 'pending' | 'confirmed' | 'missing' | 'added';
type View = 'rooms' | 'room-detail';

interface NavItem {
    id: string;
    name: string;
    quantity: number;
    weightEach: number;
    status: ItemStatus;
    isNew: boolean;
}

interface NavRoom {
    id: string;
    name: string;
    editing: boolean;   // rename mode
    note: string;
    /** The crew approved this room — a separate act from having marked every item. */
    confirmed: boolean;
    items: NavItem[];
    _newItem: string;
    _newItemSuggestions: string[];
}

@Component({
    selector: 'app-nav-inventory-editor',
    standalone: true,
    imports: [CommonModule, FormsModule],
    styles: [`
        :host { display: block; margin-bottom: 1rem; }

        /* ── Summary bar ── */
        .summary-bar {
            background: #1a1a1a; color: white;
            border-radius: 10px 10px 0 0;
            padding: 0.75rem 1.1rem;
            display: flex; align-items: center; gap: 0.75rem; flex-wrap: wrap;
        }
        .summary-label { font-size: 0.75rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; opacity: 0.65; }
        .summary-val { font-size: 0.95rem; font-weight: 700; }
        .summary-delta {
            font-size: 0.8rem; padding: 0.15rem 0.55rem; border-radius: 12px; font-weight: 700;
        }
        .delta-plus  { background: #fee2e2; color: #b91c1c; }
        .delta-minus { background: #dcfce7; color: #15803d; }
        .delta-zero  { background: rgba(255,255,255,0.15); color: rgba(255,255,255,0.7); }
        .summary-right { margin-left: auto; font-size: 0.78rem; opacity: 0.6; }

        /* ── Card wrapper ── */
        .inv-card {
            background: white; border-radius: 0 0 10px 10px;
            box-shadow: 0 1px 4px rgba(0,0,0,0.07); overflow: hidden;
        }

        /* ── Room list ── */
        .room-row {
            display: flex; align-items: center; gap: 0.75rem;
            padding: 0.95rem 1.1rem; border-bottom: 1px solid #f0f0f0;
            cursor: pointer; user-select: none; -webkit-user-select: none;
        }
        .room-row:last-child { border-bottom: none; }
        .room-row:active { background: #f9f9f9; }
        .room-row.approved { background: #f4faf4; }
        .room-icon { color: #888; font-size: 1rem; flex-shrink: 0; }
        .room-row.approved .room-icon { color: #2d6a2d; }
        .room-badge {
            flex-shrink: 0; background: #2d6a2d; color: white;
            font-size: 0.68rem; font-weight: 700; text-transform: uppercase;
            letter-spacing: 0.04em; padding: 0.2rem 0.5rem; border-radius: 10px;
        }
        /* Unapproved rooms have to be findable while scrolling a long list — see
           outstandingRoomNames. */
        .room-badge.pending { background: #b45309; }
        .scope-hint {
            font-size: 0.8rem; color: #b45309; text-align: center;
            padding: 0.45rem 1.1rem 0; margin: 0; line-height: 1.4;
        }
        .room-info { flex: 1; min-width: 0; }
        .room-name { font-size: 1rem; font-weight: 600; color: #1a1a1a; }
        .room-sub { font-size: 0.8rem; color: #888; margin-top: 0.1rem; }
        .room-sub.done { color: #2d6a2d; font-weight: 600; }
        .room-chevron { color: #bbb; font-size: 0.85rem; }

        /* Add room row */
        .add-room-row {
            padding: 0.75rem 1.1rem;
            border-top: 1px solid #f0f0f0;
            display: flex; align-items: center; gap: 0.5rem;
        }
        .add-room-input {
            flex: 1; padding: 0.55rem 0.75rem;
            border: 1.5px solid #e0e0e0; border-radius: 8px;
            font-size: 0.9rem; font-family: inherit;
        }
        .add-room-input:focus { outline: none; border-color: #2d6a2d; }
        .add-room-btn {
            padding: 0.55rem 1rem; background: #2d6a2d; color: white;
            border: none; border-radius: 8px; font-size: 0.9rem; font-weight: 600;
            cursor: pointer; font-family: inherit;
        }
        .add-room-btn:disabled { background: #b0b8b0; cursor: not-allowed; }

        /* ── Room detail ── */
        .room-detail-header {
            display: flex; align-items: center; gap: 0.75rem;
            padding: 0.85rem 1.1rem; background: #f9f9f9;
            border-bottom: 1px solid #f0f0f0;
        }
        .back-btn {
            background: none; border: 1px solid #e0e0e0; color: #555;
            min-height: 44px; padding: 0 0.85rem; border-radius: 8px;
            font-size: 0.85rem; cursor: pointer; font-family: inherit;
            display: flex; align-items: center; gap: 0.35rem;
        }
        .back-btn:active { background: #f0f0f0; }
        .room-detail-name { flex: 1; font-size: 1rem; font-weight: 700; color: #1a1a1a; }
        .rename-input {
            flex: 1; padding: 0.35rem 0.6rem;
            border: 1.5px solid #2d6a2d; border-radius: 6px;
            font-size: 1rem; font-family: inherit; font-weight: 700;
        }
        .rename-input:focus { outline: none; }
        .rename-btn {
            background: none; border: none; color: #2d6a2d;
            min-width: 44px; min-height: 44px; padding: 0 0.6rem;
            display: inline-flex; align-items: center; justify-content: center;
            font-size: 0.9rem; font-weight: 600; font-family: inherit; cursor: pointer;
            border-radius: 8px;
        }
        .rename-btn:active { background: #e6f1e6; }

        /* ── Items ── */
        .item-row {
            display: flex; align-items: center; gap: 0.5rem;
            padding: 0.75rem 1.1rem; border-bottom: 1px solid #f5f5f5;
        }
        .item-row:last-child { border-bottom: none; }
        .item-name {
            flex: 1; font-size: 0.95rem; color: #222; min-width: 0;
            white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
        }
        .item-name.missing { text-decoration: line-through; color: #bbb; }

        /* Qty stepper */
        .qty-row { display: flex; align-items: center; gap: 0.25rem; }
        .qty-btn {
            width: 32px; height: 32px; border-radius: 6px;
            border: 1.5px solid #e0e0e0; background: white;
            font-size: 1rem; font-weight: 700; cursor: pointer; color: #333;
            display: flex; align-items: center; justify-content: center;
        }
        .qty-btn:active { background: #f0f0f0; }
        .qty-val { min-width: 22px; text-align: center; font-size: 0.9rem; font-weight: 600; }

        /* Status chips — BIG so they're easy to tap */
        .status-chips { display: flex; gap: 0.35rem; }
        .schip {
            padding: 0.5rem 0.75rem; border-radius: 8px;
            border: 2px solid #e0e0e0; background: white;
            font-size: 0.85rem; font-weight: 600; cursor: pointer;
            font-family: inherit; white-space: nowrap; line-height: 1;
            min-width: 44px; text-align: center;
        }
        .schip:active { transform: scale(0.95); }
        .schip.ok.active   { border-color: #2d6a2d; background: #f0f7f0; color: #2d6a2d; }
        .schip.miss.active { border-color: #dc2626; background: #fef2f2; color: #dc2626; }
        .schip.ok:not(.active):hover   { border-color: #2d6a2d; }
        .schip.miss:not(.active):hover { border-color: #dc2626; }

        /* Add item */
        .add-item-wrap { padding: 0.65rem 1.1rem; position: relative; }
        .add-item-row { display: flex; gap: 0.5rem; }
        .add-item-input {
            flex: 1; padding: 0.6rem 0.75rem;
            border: 1.5px solid #e0e0e0; border-radius: 8px;
            font-size: 0.9rem; font-family: inherit;
        }
        .add-item-input:focus { outline: none; border-color: #2d6a2d; }
        .add-item-btn {
            padding: 0.6rem 1rem; background: #1a1a1a; color: white;
            border: none; border-radius: 8px; font-size: 0.9rem; font-weight: 600;
            cursor: pointer; font-family: inherit;
        }
        .add-item-btn:disabled { background: #b0b8b0; cursor: not-allowed; }

        /* Weight / volume prompt for a new item (#90) */
        .add-prompt {
            margin-top: 0.6rem; padding: 0.8rem 0.9rem;
            border: 1.5px solid #2d6a2d; border-radius: 10px; background: #f6faf6;
        }
        .add-prompt-title { font-size: 0.95rem; font-weight: 700; color: #222; }
        .add-prompt-hint { font-size: 0.8rem; color: #555; margin: 0.2rem 0 0.6rem; }
        .add-prompt-fields { display: flex; gap: 0.6rem; }
        .add-prompt-field { flex: 1; display: flex; flex-direction: column; gap: 0.25rem; min-width: 0; }
        .add-prompt-field label { font-size: 0.75rem; font-weight: 600; color: #555; }
        .add-prompt-field input {
            padding: 0.6rem 0.7rem; border: 1.5px solid #e0e0e0; border-radius: 8px;
            font-size: 1rem; font-family: inherit; width: 100%; box-sizing: border-box;
        }
        .add-prompt-field input:focus { outline: none; border-color: #2d6a2d; }
        .add-prompt-actions { display: flex; gap: 0.5rem; margin-top: 0.75rem; }
        .add-prompt-actions button {
            flex: 1; min-height: 44px; border-radius: 8px; font-size: 0.9rem; font-weight: 600;
            font-family: inherit; cursor: pointer;
        }
        .add-prompt-save { background: #2d6a2d; color: white; border: none; }
        .add-prompt-save:disabled { background: #b0b8b0; cursor: not-allowed; }
        .add-prompt-skip { background: white; color: #333; border: 1.5px solid #ccc; }
        .add-prompt-cancel { flex: 0 0 auto !important; padding: 0 0.9rem; background: none; color: #666; border: 1.5px solid transparent; }

        /* Item saved without a weight — the crew skipped the estimate */
        .no-weight-tag {
            display: inline-block; margin-left: 0.4rem; padding: 0.05rem 0.45rem;
            border-radius: 10px; background: #fef3c7; color: #92400e;
            font-size: 0.7rem; font-weight: 700; vertical-align: middle;
        }

        /* Autocomplete dropdown */
        .autocomplete-list {
            position: absolute; left: 1.1rem; right: 1.1rem;
            background: white; border: 1.5px solid #e0e0e0;
            border-radius: 0 0 8px 8px; z-index: 100;
            box-shadow: 0 4px 12px rgba(0,0,0,0.1);
            max-height: 200px; overflow-y: auto;
        }
        .autocomplete-item {
            padding: 0.7rem 0.9rem; cursor: pointer; font-size: 0.9rem; color: #222;
            border-bottom: 1px solid #f5f5f5;
        }
        .autocomplete-item:last-child { border-bottom: none; }
        .autocomplete-item:active { background: #f0f7f0; }
        .autocomplete-item strong { color: #2d6a2d; }

        /* Room approved */
        .room-approved-banner {
            display: flex; align-items: center; gap: 0.5rem;
            margin: 0 1.1rem 1rem; padding: 0.8rem;
            background: #f0f7f0; border-radius: 8px;
            color: #2d6a2d; font-weight: 700; font-size: 0.95rem;
        }
        /* Outlined secondary buttons, 44px (#100b). They were underlined text links: a small
           target that did not read as tappable on a phone. */
        .reopen-btn, .unconfirm-btn {
            margin-left: auto; min-height: 44px; padding: 0 0.9rem;
            display: inline-flex; align-items: center; gap: 0.4rem;
            background: white; border: 1.5px solid #2d6a2d; border-radius: 8px;
            color: #2d6a2d; font-size: 0.88rem; font-weight: 600;
            font-family: inherit; cursor: pointer; white-space: nowrap;
        }
        .reopen-btn:active, .unconfirm-btn:active { background: #e6f1e6; }

        /* Room note */
        .room-note-wrap { padding: 0.5rem 1.1rem 0.85rem; }
        .note-input {
            width: 100%; box-sizing: border-box; padding: 0.5rem 0.75rem;
            border: 1.5px solid #e0e0e0; border-radius: 8px;
            font-size: 0.85rem; font-family: inherit; resize: none; color: #555;
        }
        .note-input:focus { outline: none; border-color: #2d6a2d; }

        /* Confirm room button */
        .confirm-room-btn {
            display: flex; align-items: center; justify-content: center; gap: 0.5rem;
            width: calc(100% - 2.2rem); margin: 0 1.1rem 1rem;
            padding: 0.9rem; background: #2d6a2d; color: white;
            border: none; border-radius: 8px; font-size: 1rem; font-weight: 700;
            cursor: pointer; font-family: inherit;
        }
        .confirm-room-btn:active { background: #1f4d1f; }
        .confirm-room-btn:disabled { background: #b0b8b0; cursor: not-allowed; }

        /* Confirm scope */
        .confirm-scope-btn {
            display: flex; align-items: center; justify-content: center; gap: 0.5rem;
            width: 100%; padding: 0.9rem; margin-top: 0;
            background: #1a1a1a; color: white;
            border: none; border-radius: 0 0 10px 10px;
            font-size: 1rem; font-weight: 700; cursor: pointer; font-family: inherit;
        }
        .confirm-scope-btn:disabled { background: #b0b8b0; cursor: not-allowed; }
        .confirmed-banner {
            display: flex; align-items: center; gap: 0.65rem;
            background: #f0f7f0; border-radius: 0 0 10px 10px;
            padding: 0.9rem 1.1rem; color: #2d6a2d; font-weight: 600; font-size: 0.95rem;
        }
        /* .unconfirm-btn ("Make changes") shares the .reopen-btn style above. Still pushed to the
           right and outlined rather than filled: re-opening the scope is a correction, not a step
           in the flow, and it should not compete with the confirmation it sits on. */

        .no-inv { padding: 2rem; text-align: center; color: #aaa; font-size: 0.9rem; }
    `],
    template: `
        <!-- ── SUMMARY BAR ─────────────────────────────── -->
        <div class="summary-bar">
            <div>
                <div class="summary-label">Weight</div>
                <div class="summary-val">{{ currentWeight | number:'1.0-0' }} lbs</div>
            </div>
            <div class="summary-delta"
                [class.delta-plus]="weightDelta > 0"
                [class.delta-minus]="weightDelta < 0"
                [class.delta-zero]="weightDelta === 0">
                {{ weightDelta === 0 ? '=' : (weightDelta > 0 ? '+' : '') }}{{ weightDelta | number:'1.0-0' }} lbs
            </div>
            <div class="summary-right">
                {{ reviewedCount }}/{{ rooms.length }} rooms reviewed
            </div>
        </div>

        <!-- ── ROOM LIST VIEW ─────────────────────────────── -->
        <ng-container *ngIf="view === 'rooms'">
            <div class="inv-card">
                <div class="no-inv" *ngIf="rooms.length === 0">
                    <i class="pi pi-box" style="font-size:1.5rem;display:block;margin-bottom:0.5rem;"></i>
                    No inventory loaded yet.
                </div>

                <div class="room-row" *ngFor="let r of rooms"
                    [class.approved]="r.confirmed"
                    (click)="openRoom(r)">
                    <i class="pi room-icon"
                        [class.pi-check-circle]="r.confirmed"
                        [class.pi-home]="!r.confirmed"></i>
                    <div class="room-info">
                        <div class="room-name">{{ r.name }}</div>
                        <div class="room-sub" [class.done]="r.confirmed">
                            {{ roomSummary(r) }}
                        </div>
                    </div>
                    <span class="room-badge" *ngIf="r.confirmed">Approved</span>
                    <span class="room-badge pending" *ngIf="!r.confirmed">Not approved</span>
                    <i class="pi pi-chevron-right room-chevron"></i>
                </div>

                <!-- Add new room -->
                <div class="add-room-row" *ngIf="!scopeConfirmed">
                    <input class="add-room-input"
                        [(ngModel)]="newRoomName"
                        [ngModelOptions]="{standalone: true}"
                        placeholder="Add a room..."
                        (keyup.enter)="addRoom()" />
                    <button class="add-room-btn" (click)="addRoom()" [disabled]="!newRoomName.trim()">
                        + Room
                    </button>
                </div>
            </div>

            <!-- Confirm scope -->
            <div *ngIf="!scopeConfirmed">
                <button class="confirm-scope-btn"
                    [disabled]="!canConfirmScope"
                    (click)="confirmScope()">
                    <i class="pi pi-check"></i> Confirm Scope with Customer
                </button>
                <p *ngIf="!canConfirmScope" class="scope-hint">
                    {{ rooms.length === 0
                        ? 'Add at least one room before confirming scope'
                        : 'Still to approve: ' + outstandingRoomNames.join(', ') }}
                </p>
            </div>
            <div class="confirmed-banner" *ngIf="scopeConfirmed">
                <i class="pi pi-check-circle" style="font-size:1.2rem;"></i>
                Scope confirmed — {{ currentWeight | number:'1.0-0' }} lbs
                <button class="unconfirm-btn" type="button" (click)="unconfirmScope()">
                    <i class="pi pi-pencil"></i>Make changes
                </button>
            </div>
        </ng-container>

        <!-- ── ROOM DETAIL VIEW ──────────────────────────── -->
        <ng-container *ngIf="view === 'room-detail' && activeRoom">
            <div class="inv-card">
                <!-- Header: back + room name (editable) -->
                <div class="room-detail-header">
                    <button class="back-btn" (click)="closeRoom()">
                        <i class="pi pi-arrow-left"></i> Rooms
                    </button>
                    <ng-container *ngIf="!activeRoom.editing">
                        <span class="room-detail-name">{{ activeRoom.name }}</span>
                        <button class="rename-btn" (click)="activeRoom.editing = true; renameVal = activeRoom.name">
                            <i class="pi pi-pencil"></i>
                        </button>
                    </ng-container>
                    <ng-container *ngIf="activeRoom.editing">
                        <input class="rename-input" [(ngModel)]="renameVal" [ngModelOptions]="{standalone: true}"
                            (keyup.enter)="confirmRename()" />
                        <button class="rename-btn" (click)="confirmRename()">✓ Save</button>
                    </ng-container>
                </div>

                <!-- Item rows -->
                <div class="item-row" *ngFor="let item of activeRoom.items">
                    <div class="item-name" [class.missing]="item.status === 'missing'" [title]="item.name">
                        {{ item.name }}<span class="no-weight-tag" *ngIf="item.isNew && item.weightEach === 0">no weight</span>
                    </div>

                    <!-- Qty stepper (hidden when missing) -->
                    <div class="qty-row" *ngIf="item.status !== 'missing'">
                        <button class="qty-btn" (click)="decQty(item)"
                            [disabled]="item.quantity <= 1 || scopeConfirmed">−</button>
                        <span class="qty-val">{{ item.quantity }}</span>
                        <button class="qty-btn" (click)="incQty(item)"
                            [disabled]="scopeConfirmed">+</button>
                    </div>

                    <!-- Status chips — big tap targets -->
                    <div class="status-chips">
                        <button class="schip ok"
                            [class.active]="item.status === 'confirmed' || item.status === 'added'"
                            [disabled]="scopeConfirmed"
                            (click)="setStatus(item, 'confirmed')">✓</button>
                        <button class="schip miss"
                            [class.active]="item.status === 'missing'"
                            [disabled]="scopeConfirmed"
                            (click)="setStatus(item, 'missing')">✗</button>
                    </div>
                </div>

                <!-- Add item with autocomplete -->
                <div class="add-item-wrap">
                    <div class="add-item-row">
                        <input class="add-item-input"
                            [(ngModel)]="activeRoom._newItem"
                            [ngModelOptions]="{standalone: true}"
                            placeholder="Add item..."
                            (input)="onItemInput(activeRoom)"
                            (keyup.enter)="addItem(activeRoom)"
                            (blur)="delayHideSuggestions()" />
                        <button class="add-item-btn"
                            [disabled]="!activeRoom._newItem.trim()"
                            (click)="addItem(activeRoom)">Add</button>
                    </div>

                    <!-- Approximate weight and volume, asked before the item is added (#90) -->
                    <ng-container *ngIf="pendingAdd as p">
                    <div class="add-prompt" *ngIf="p.roomId === activeRoom.id">
                        <div class="add-prompt-title">{{ p.name }}</div>
                        <div class="add-prompt-hint">
                            {{ p.fromCatalog
                                ? 'Typical values from the item list — change them if this one is different.'
                                : 'Not in the item list. Enter an approximate weight and volume, or skip.' }}
                        </div>
                        <div class="add-prompt-fields">
                            <div class="add-prompt-field">
                                <label for="nav-add-weight">Weight (lbs)</label>
                                <input id="nav-add-weight" type="number" inputmode="decimal" min="0"
                                    [(ngModel)]="p.weight" [ngModelOptions]="{standalone: true}" />
                            </div>
                            <div class="add-prompt-field">
                                <label for="nav-add-volume">Volume (cu ft)</label>
                                <input id="nav-add-volume" type="number" inputmode="decimal" min="0"
                                    [(ngModel)]="p.volume" [ngModelOptions]="{standalone: true}" />
                            </div>
                        </div>
                        <div class="add-prompt-actions">
                            <button class="add-prompt-cancel" (click)="cancelAdd()">Cancel</button>
                            <button class="add-prompt-skip" *ngIf="!p.fromCatalog"
                                (click)="commitAdd(activeRoom, true)">Skip</button>
                            <button class="add-prompt-save"
                                [disabled]="!canCommitAdd"
                                (click)="commitAdd(activeRoom, false)">Add item</button>
                        </div>
                    </div>
                    </ng-container>

                    <!-- Autocomplete dropdown -->
                    <div class="autocomplete-list" *ngIf="activeRoom._newItemSuggestions.length > 0">
                        <div class="autocomplete-item"
                            *ngFor="let s of activeRoom._newItemSuggestions"
                            (mousedown)="selectSuggestion(activeRoom, s)">
                            <span [innerHTML]="highlight(s, activeRoom._newItem)"></span>
                        </div>
                    </div>
                </div>

                <!--
                    Room note — HIDDEN, not deleted. NOTES_ENABLED is a compile-time false, so
                    this block never renders.

                    It saves nothing: anything typed here is discarded on navigation, the same
                    write-nothing failure as #14 and #15. Rather than ship a box that quietly
                    eats the crew's input, it is switched off until P7 is answered.

                    Do not simply flip the flag. The obvious storage — moverNotes — belongs to
                    the Yembo surveyor and is what this box is pre-filled from, so writing back
                    would replace pre-move survey context with day-of reporting. Read P7 in
                    docs/crew-navigator.md first; the storage follows from who is meant to read
                    these notes, which is the open part.
                -->
                <div class="room-note-wrap" *ngIf="NOTES_ENABLED">
                    <textarea class="note-input" rows="2"
                        [(ngModel)]="activeRoom.note"
                        [ngModelOptions]="{standalone: true}"
                        placeholder="Notes: tight doorway, fragile items, antiques...">
                    </textarea>
                </div>

                <!-- Confirm room — auto-navigates back to list -->
                <button class="confirm-room-btn"
                    *ngIf="!activeRoom.confirmed"
                    [disabled]="!isRoomDone(activeRoom) || scopeConfirmed"
                    (click)="confirmRoom(activeRoom)">
                    <i class="pi pi-check"></i>
                    {{ isRoomDone(activeRoom) ? 'Approve Room' : 'Mark every item first' }}
                </button>
                <div class="room-approved-banner" *ngIf="activeRoom.confirmed">
                    <i class="pi pi-check-circle"></i>
                    Room approved
                    <button class="reopen-btn" *ngIf="!scopeConfirmed"
                        (click)="reopenRoom(activeRoom)"><i class="pi pi-undo"></i>Undo</button>
                </div>
            </div>
        </ng-container>
    `,
})
export class NavInventoryEditorComponent extends NavBaseComponent implements OnInit, OnDestroy {

    rooms: NavRoom[] = [];
    originalWeight = 0;
    scopeConfirmed = false;
    view: View = 'rooms';
    activeRoom: NavRoom | null = null;
    newRoomName = '';
    renameVal = '';

    /**
     * The per-room notes box is switched off until P7 decides where crew notes are stored and
     * who reads them. Kept in the template rather than deleted so the decision has something to
     * turn back on — but it must not ship visible while it saves nothing.
     */
    readonly NOTES_ENABLED = false;

    /** The item whose weight/volume prompt is open, if any (#90). */
    pendingAdd: PendingAdd | null = null;

    private _eventId: string | null = null;
    private _catalog: () => CatalogItem[] = () => [];
    private _warnedNoGate = false;
    private _lastInventorySignature: string | null = null;
    private _sub: Subscription | null = null;
    private _activeRoomId: string | null = null;
    private _reviewTimer: ReturnType<typeof setTimeout> | null = null;
    private _review: ReviewState = { originalWeight: null, items: {}, rooms: {}, scopeConfirmed: false, missingSnapshot: {} };

    constructor(private store: Store, private cdr: ChangeDetectorRef) { super(); }

    // ── Computed properties ───────────────────────────────────────────────────

    get currentWeight(): number {
        return this.rooms.reduce((t, r) =>
            t + r.items.filter(i => i.status !== 'missing')
                .reduce((s, i) => s + i.quantity * i.weightEach, 0), 0);
    }
    get weightDelta(): number { return this.currentWeight - this.originalWeight; }

    /**
     * Rooms the crew has *approved*, not rooms whose items happen to all be ticked.
     *
     * Approving a room is a second, deliberate act — ticking every item only enables the
     * button. Counting `isRoomDone` here would have marked a room done the moment the last
     * item was ticked, so the crew could never tell the two states apart on the room list.
     */
    get reviewedCount(): number { return this.rooms.filter(r => r.confirmed).length; }
    get isSatisfied(): boolean { return this.scopeConfirmed; }

    /**
     * Scope can only be confirmed once every room has been reviewed *and* there is at least
     * one room to review.
     *
     * The room count guard is the whole point: `reviewedCount < rooms.length` is `0 < 0` on a
     * job with no inventory, which is false, so the button used to enable itself immediately
     * and the step passed with nothing done — a silent auto-approval, not a block (#19).
     *
     * Deliberately *not* a dead end, unlike crew check-in: this component can add rooms and
     * items, so a crew facing an empty inventory builds it on the spot rather than waiting for
     * the office. A crew that adds a single empty room can still get through — that is an
     * explicit act on the record, which is the point, where the old behaviour required nothing
     * at all.
     */
    get canConfirmScope(): boolean {
        return this.rooms.length > 0 && this.reviewedCount === this.rooms.length;
    }

    /**
     * Rooms still waiting on approval, named.
     *
     * The hint used to read "Review all 5 rooms first", which is true and useless: on a phone
     * the room list scrolls, so a crew that has approved four of five sees only approved rooms
     * on screen, concludes the button is broken, and has no way to find the one they missed.
     * Blocking without saying what is missing is the same failure the crew empty-state had —
     * see the note under P1.
     */
    get outstandingRoomNames(): string[] {
        return this.rooms.filter(r => !r.confirmed).map(r => r.name);
    }

    /**
     * An empty room counts as reviewed. There is nothing in it to tick, and the navigator has
     * no remove-room control — so without this an accidental "+ Room" would wedge the step
     * with no way back out.
     */
    isRoomDone(r: NavRoom): boolean {
        return r.items.every(i => i.status !== 'pending');
    }

    /**
     * Three distinct states, because ticking every item and approving the room are not the
     * same thing and the crew could not previously tell them apart — the list said
     * "4 of 4 items confirmed" whether or not the room had ever been approved.
     */
    roomSummary(r: NavRoom): string {
        const missing = r.items.filter(i => i.status === 'missing').length;
        const missingNote = missing > 0 ? `, ${missing} missing` : '';

        if (r.confirmed) {
            const present = r.items.filter(i => i.status !== 'missing').length;
            return `${present} of ${r.items.length} items${missingNote}`;
        }
        if (this.isRoomDone(r)) {
            return r.items.length === 0
                ? 'Empty — tap to approve'
                : `All items marked${missingNote} — tap to approve`;
        }
        const pending = r.items.filter(i => i.status === 'pending').length;
        return `${pending} items to review`;
    }

    // ── Lifecycle ─────────────────────────────────────────────────────────────

    ngOnInit() {
        this._eventId = this.store.selectSignal(selectRouteParam('eventId'))() ?? null;
        // The same franchise catalog the job page's Add Item searches (job-inventory.effects.ts).
        const catalog = this.store.selectSignal(createConfigSelector('INVENTORY_ITEMS'));
        this._catalog = () => (catalog() ?? []).filter((c): c is CatalogItem => !!c);

        this._restoreReview();

        // Live, not the one-shot `.subscribe().unsubscribe()` this used to be. The component
        // is now a *view* over the inventory slice rather than the owner of a private copy:
        // edits dispatch InventoryActions, so the store stays authoritative and a remote
        // update from the office refreshes what the crew sees instead of being silently
        // discarded by a snapshot taken at mount.
        this._sub = this.store.select(jobInventoryFeature.selectInventoryEditor)
            // eslint-disable-next-line @ngrx/no-store-subscription
            .subscribe(inv => {
                // Only rebuild when the inventory itself actually changed.
                //
                // This slice is rebuilt wholesale from the job's fields on every `jobLoaded` /
                // `remoteJobUpdated`, and the crew's own review writes cause exactly such an
                // update — each tick round-trips, the server publishes `jobChanged`, and the
                // slice re-emits with identical contents. Re-deriving `rooms` on those meant
                // rebuilding every room, and its approval, from a parallel id-keyed map many
                // times per minute, for no change at all. Any weakness in that mapping then
                // showed up as approvals and statuses flickering on their own — worse on a
                // slow connection, invisible on a fast one, which is exactly backwards from
                // how it should behave.
                const signature = this._inventorySignature(inv?.rooms ?? []);
                if (signature !== this._lastInventorySignature) {
                    this._lastInventorySignature = signature;
                    this.rooms = (inv?.rooms ?? []).map(r => this._buildRoomFromStore(r));
                }

                // Weight as it stood before the crew touched anything, so the delta means
                // "what this crew changed". Captured once and then persisted, otherwise a
                // mid-move reload would rebase it and silently report a delta of zero.
                if (this._review.originalWeight == null) {
                    this._review = { ...this._review, originalWeight: this.currentWeight };
                    this._persistReview();
                }
                this.originalWeight = this._review.originalWeight;

                // Keep the open room pointing at the rebuilt object, not the stale one.
                this.activeRoom = this._activeRoomId
                    ? this.rooms.find(r => r.id === this._activeRoomId) ?? null
                    : null;
                if (!this.activeRoom) { this.view = 'rooms'; }

                // Re-assert the gate on every emission, not just at init and on confirm.
                // Gate state lives in a root-provided service that other code resets and
                // re-registers, and a re-registration lands as `satisfied: false` with nothing
                // to re-ask this component. Reporting the current answer here means a reset can
                // cost at most one emission rather than stranding the crew on a dead button.
                this._notifyGate();

                this.cdr.markForCheck();
            });

        // Report the starting state to the step gate (unconfirmed, so the step stays
        // blocked until the crew presses Confirm Scope — but the gate now reflects a real
        // answer rather than an assumed one). See #19.
        this._notifyGate();
    }

    ngOnDestroy() {
        this._sub?.unsubscribe();
        // Best effort: a debounced review write that has not fired yet would otherwise be
        // lost when the crew leaves the step.
        this._flushReview();
    }

    private _notifyGate() {
        if (typeof this.node.props?.onReady === 'function') {
            this.node.props.onReady(this.isSatisfied);
            return;
        }

        // Nothing to report to means the step's gate can never be satisfied from here, so the
        // crew will confirm scope, see the banner, and find the confirm button still dead.
        // Warn once rather than fail silently — this is the exact shape of the bug that made
        // #15 invisible for so long.
        if (!this._warnedNoGate) {
            this._warnedNoGate = true;
            console.warn(
                '[NavInventoryEditor] no onReady on this node — confirming scope cannot enable '
                + 'the step\'s confirm button. See the gate registration in registry.ts.',
            );
        }
    }

    // ── Review state (per-run, persisted on the event) ────────────────────────
    //
    // Ticks are *review progress*, not inventory: a confirmed sofa is still one sofa at the
    // same weight. They therefore live in the event's `navigatorData` and never go through
    // the job save — `fields.setValue` pushes an `updated inventory` history item on every
    // write of `jobs.inventory` (fields.setValue.ts:46), so persisting ticks that way would
    // put a history line on the job for every tap.
    //
    // The full status is stored, not just the ✓ marks. Restoring only confirmations would
    // show a missing item as pending again on re-entry and make the crew re-report it.

    private _restoreReview() {
        const review = this._persistedNavigatorData()?.inventoryReview;
        if (!review) { return; }
        this._review = {
            originalWeight: review.originalWeight ?? null,
            items: review.items ?? {},
            rooms: review.rooms ?? {},
            scopeConfirmed: review.scopeConfirmed ?? false,
            missingSnapshot: review.missingSnapshot ?? {},
        };
        this.scopeConfirmed = this._review.scopeConfirmed;
    }

    private _persistedNavigatorData(): any {
        const job = this.store.selectSignal(selectJob)();
        const event = job?.events?.find((e: any) => e.id === this._eventId);
        if (!event?.navigatorData) { return null; }
        try { return JSON.parse(event.navigatorData); } catch { return null; }
    }

    /** Coalesces the taps of a burst into one mutation. */
    private _persistReview() {
        if (this._reviewTimer !== null) { clearTimeout(this._reviewTimer); }
        this._reviewTimer = setTimeout(() => this._flushReview(), REVIEW_PERSIST_DEBOUNCE_MS);
    }

    private _flushReview() {
        if (this._reviewTimer !== null) {
            clearTimeout(this._reviewTimer);
            this._reviewTimer = null;
        }
        if (!this._eventId) { return; }

        // A COPY, never the live object.
        //
        // `provideStore` is configured without explicit `runtimeChecks`, so NgRx's
        // `strictActionImmutability` default applies and it **deep-freezes every action
        // payload**. Passing `this._review` by reference froze it, along with its `rooms` and
        // `items` maps, the moment the first patch was dispatched. Every later write then threw
        // `TypeError: Cannot add property …, object is not extensible` — from inside
        // `confirmRoom`, *after* it had set the room's `confirmed` flag and *before* it reached
        // this method or `closeRoom()`.
        //
        // The result was a component that looked like it was working and had silently stopped:
        // the "Room approved" banner appeared, no request was sent, and the crew was not
        // returned to the room list. Exactly one approval survived per visit, because remounting
        // rebuilt `_review` from JSON and unfroze it. It cost days.
        this.store.dispatch(quickJsActions.navigatorDataPatched({
            eventId: this._eventId,
            patch: { inventoryReview: structuredClone(this._review) },
        }));
    }

    /**
     * Everything the editor renders or keys review state on. Two emissions with the same
     * signature are the same inventory as far as this component is concerned, so the view can
     * be left alone — including the crew's in-progress approvals.
     */
    private _inventorySignature(rooms: InventoryRoom[]): string {
        return rooms
            .map(r => [
                r.id,
                r.name,
                (r.inventory ?? []).map(i => `${i.id},${i.name},${i.quantity},${i.weight ?? 0}`).join('|'),
            ].join('~'))
            .join(';');
    }

    private _buildRoomFromStore(r: InventoryRoom): NavRoom {
        return {
            id: r.id,
            name: r.name,
            editing: false,
            note: r.moverNotes ?? r.notes ?? '',
            confirmed: this._review.rooms[r.id] === true,
            _newItem: '',
            _newItemSuggestions: [],
            items: (r.inventory ?? []).map((it: InventoryItem) => ({
                id: it.id,
                name: it.name,
                quantity: it.quantity ?? 1,
                weightEach: it.weight ?? 0,
                status: this._review.items[it.id] ?? 'pending',
                isNew: it.navStatus === 'added',
            })),
        };
    }

    // ── Room navigation ───────────────────────────────────────────────────────

    openRoom(r: NavRoom) {
        this._activeRoomId = r.id;
        this.activeRoom = r;
        this.view = 'room-detail';
    }

    closeRoom() {
        this._activeRoomId = null;
        this.activeRoom = null;
        this.view = 'rooms';
    }

    /**
     * Approving a room is the crew's second act, after every item is marked. It is what the
     * room list and the scope gate count — see `reviewedCount`.
     */
    confirmRoom(r: NavRoom) {
        r.confirmed = true;
        this._review = { ...this._review, rooms: { ...this._review.rooms, [r.id]: true } };
        this._flushReview();
        this.closeRoom();
    }

    reopenRoom(r: NavRoom) {
        r.confirmed = false;
        const rooms = { ...this._review.rooms };
        delete rooms[r.id];
        this._review = { ...this._review, rooms };
        this._flushReview();
    }

    addRoom() {
        const name = this.newRoomName.trim();
        if (!name) return;

        const roomId = generateUUID();

        // Claim the room id *before* dispatching. `dispatch` runs the store subscription
        // synchronously, and that handler re-resolves `activeRoom` from `_activeRoomId` — so
        // assigning it afterwards would leave `activeRoom` null and render an empty detail view.
        this._activeRoomId = roomId;
        this.view = 'room-detail';

        this.store.dispatch(InventoryActions.roomAdded({
            room: {
                id: roomId,
                name,
                totalVolume: 0,
                totalWeight: 0,
                totalItems: 0,
                totalBoxes: 0,
                inventory: [],
            },
        }));

        this.newRoomName = '';
    }

    confirmRename() {
        if (!this.activeRoom) return;
        const name = this.renameVal.trim();
        if (name) {
            this.store.dispatch(InventoryActions.editRoomNameInPlaceConfirmButtonClicked({
                roomId: this.activeRoom.id,
                name,
            }));
        }
        this.activeRoom.editing = false;
    }

    // ── Item operations ───────────────────────────────────────────────────────

    /**
     * Status is review state only — it does not touch the inventory until the crew confirms
     * scope. Before the first confirm, un-marking a missing item is therefore free: the quantity
     * was never changed. After a confirm it was, and `confirmScope` / `unconfirmScope` restore it
     * from `missingSnapshot` (**#97**) — not this method, which only records what the crew sees.
     */
    setStatus(item: NavItem, status: ItemStatus) {
        // Tapping the active status deselects back to pending
        item.status = item.status === status ? 'pending' : status;

        const items = { ...this._review.items };
        if (item.status === 'pending') {
            delete items[item.id];
        } else {
            items[item.id] = item.status;
        }
        this._review = { ...this._review, items };

        this._revokeApprovalFor(item.id);
        this._persistReview();
    }

    /**
     * An approval covers the room as it stood when it was given. Change anything in the room
     * afterwards and that approval no longer describes reality, so it is withdrawn and the crew
     * has to approve again.
     *
     * Without this, un-ticking an item in an approved room left the room showing *Approved* and
     * still counting toward the scope gate — the crew could confirm scope over an inventory
     * nobody had agreed to in that state.
     */
    private _revokeApprovalFor(_itemId: string) {
        // Uses the open room, not a search for whichever room contains this item id. Items are
        // only ever edited from inside a room's detail view, so the open room IS the room being
        // changed — and a search would revoke the wrong room's approval the moment two rooms
        // shared an item id, which nothing guarantees they do not.
        const room = this.activeRoom;
        if (room?.confirmed) { this.reopenRoom(room); }
    }

    incQty(item: NavItem) { this._setQty(item, item.quantity + 1); }
    decQty(item: NavItem) { if (item.quantity > 1) { this._setQty(item, item.quantity - 1); } }

    private _setQty(item: NavItem, quantity: number) {
        // The open room, for the same reason as `_revokeApprovalFor`: searching by item id
        // would send the quantity change to whichever room happened to match first.
        const room = this.activeRoom;
        if (!room) { return; }
        // Changing a count changes the scope the room was approved for.
        if (room.confirmed) { this.reopenRoom(room); }
        this.store.dispatch(InventoryActions.itemQuantityInputChanged({
            roomId: room.id,
            itemId: item.id,
            quantity,
        }));
    }

    /**
     * Step one of adding an item: open the weight/volume prompt (#90). Nothing is dispatched yet.
     *
     * Items used to go in at 0 lbs / 0 cu ft, so every crew-added item silently left the move
     * weight unchanged. The job page fills both from the franchise catalog; this does the same
     * when the name matches an entry, and otherwise asks the crew for an estimate — which they
     * may skip, because an unknown item should not block the walkthrough.
     */
    addItem(room: NavRoom) {
        const name = room._newItem.trim();
        if (!name) return;

        const match = this._findCatalogItem(name);
        const weight = match ? this._numberText(match.weight) : '';
        const volume = match ? this._numberText(match.volume) : '';
        this.pendingAdd = {
            roomId: room.id,
            name: match ? this._catalogName(match) || name : name,
            weight,
            volume,
            // A catalog entry with a blank value is treated as unknown, so Skip stays available.
            fromCatalog: !!match && weight !== '' && volume !== '',
        };

        room._newItem = '';
        room._newItemSuggestions = [];
    }

    /** Both values must be non-negative numbers; an empty field only passes through Skip. */
    get canCommitAdd(): boolean {
        const p = this.pendingAdd;
        return !!p && this._parseAmount(p.weight) !== null && this._parseAmount(p.volume) !== null;
    }

    cancelAdd() {
        this.pendingAdd = null;
    }

    /** Step two: add the item. `skip` saves it at 0 / 0, and the row shows a "no weight" tag. */
    commitAdd(room: NavRoom, skip: boolean) {
        const p = this.pendingAdd;
        if (!p || p.roomId !== room.id) return;
        if (!skip && !this.canCommitAdd) return;

        const itemId = generateUUID();
        // A new item changes the scope the room was approved for.
        if (room.confirmed) { this.reopenRoom(room); }

        // An item the crew just added has by definition been seen by them. Recorded *before*
        // the dispatch: `dispatch` runs the store subscription synchronously, which rebuilds the
        // room from `_review` — recorded after, the new row came up `pending` until some later
        // rebuild happened to pick it up.
        this._review = { ...this._review, items: { ...this._review.items, [itemId]: 'added' } };

        this.store.dispatch(InventoryActions.itemAdded({
            roomId: room.id,
            item: {
                id: itemId,
                name: p.name,
                quantity: 1,
                // Per unit, as on the job page.
                weight: skip ? 0 : this._parseAmount(p.weight) ?? 0,
                volume: skip ? 0 : this._parseAmount(p.volume) ?? 0,
                // Marks this as something the crew found on the day that the survey missed.
                navStatus: 'added',
            },
        }));

        this._persistReview();

        this.pendingAdd = null;
    }

    private _catalogName(c: CatalogItem): string {
        return (c.reviewerName || c.label || '').trim();
    }

    /** Exact, case-insensitive name match — a partial match would prefill the wrong item's weight. */
    private _findCatalogItem(name: string): CatalogItem | undefined {
        const key = name.toLowerCase();
        return this._catalog().find(c =>
            this._catalogName(c).toLowerCase() === key || (c.label ?? '').trim().toLowerCase() === key);
    }

    /** Catalog values are strings ("42", "6.5"); unparseable ones prefill nothing. */
    private _numberText(value: string | null | undefined): string {
        const n = parseFloat(value ?? '');
        return Number.isFinite(n) && n >= 0 ? String(n) : '';
    }

    private _parseAmount(value: string | number | null | undefined): number | null {
        if (value === null || value === undefined || String(value).trim() === '') return null;
        const n = typeof value === 'number' ? value : parseFloat(value);
        return Number.isFinite(n) && n >= 0 ? n : null;
    }

    // ── Autocomplete ──────────────────────────────────────────────────────────

    onItemInput(room: NavRoom) {
        const q = room._newItem.trim().toLowerCase();
        if (q.length < 1) { room._newItemSuggestions = []; return; }

        // Build candidate list: existing item names from all rooms, the franchise catalog (#90 —
        // picking one of these prefills its weight), then the built-in common items
        const existingNames = this.rooms.flatMap(r => r.items.map(i => i.name));
        const catalogNames = this._catalog().map(c => this._catalogName(c)).filter(Boolean);
        const allSuggestions = [...new Set([...existingNames, ...catalogNames, ...COMMON_ITEMS])];

        room._newItemSuggestions = allSuggestions
            .filter(s => s.toLowerCase().includes(q))
            .slice(0, 6);
    }

    selectSuggestion(room: NavRoom, suggestion: string) {
        room._newItem = suggestion;
        room._newItemSuggestions = [];
        this.addItem(room);
    }

    delayHideSuggestions() {
        setTimeout(() => {
            if (this.activeRoom) this.activeRoom._newItemSuggestions = [];
        }, 150);
    }

    highlight(text: string, query: string): string {
        if (!query) return text;
        const idx = text.toLowerCase().indexOf(query.toLowerCase());
        if (idx === -1) return text;
        return text.slice(0, idx)
            + `<strong>${text.slice(idx, idx + query.length)}</strong>`
            + text.slice(idx + query.length);
    }

    // ── Scope confirmation ────────────────────────────────────────────────────

    /**
     * The one point where the crew's reconciliation reaches the job.
     *
     * Everything before this is either a store edit awaiting save (quantities, added items,
     * rooms) or pure review state on the event (ticks). Confirming applies the missing marks
     * to the inventory and then sends the lot as one navigator save through the outbox (#86c):
     * the inventory changes per item, and the review marks in the same write.
     *
     * This used to call `node.props.onScopeConfirmed(...)` and nothing else. That callback was
     * never assigned by anything — `<InventoryEditor />` is declared bare in the script and
     * `registry.ts` injects only `onReady` — and it could not have been, because script-declared
     * functions do not survive `ctx.dump()` at the QuickJS boundary, the same constraint that
     * made `<Button>`'s `click` inert in #24. So every edit the crew made died with the
     * component. See #15.
     */
    confirmScope() {
        // Missing items become quantity 0, which drops them out of every weight, volume and
        // count total automatically — no consumer needs to know `navStatus` exists. The
        // original weight is left intact so what was *expected* on site is still on record.
        // What the item held before is recorded in `missingSnapshot` so it can be given back.
        //
        // `itemEdited` REPLACES the item rather than merging into it, so the stored item has to
        // be spread first. Building a fresh object from the view model instead dropped every
        // field the navigator does not display — volume, the iso codes, the isBulky/isValuable
        // flags — and losing `volume` alone turns the volume totals into NaN, because
        // `selectInventoryTotals` multiplies it by quantity unguarded.
        const storedRooms = this.store.selectSignal(jobInventoryFeature.selectInventoryEditor)().rooms ?? [];
        const snapshot = { ...this._review.missingSnapshot };

        for (const room of this.rooms) {
            const storedRoom = storedRooms.find(r => r.id === room.id);

            for (const item of room.items) {
                const stored = storedRoom?.inventory?.find(i => i.id === item.id);
                if (!stored) { continue; }

                if (item.status === 'missing') {
                    // Only snapshot a live value. An item already stored as missing is a reload
                    // after "Make changes" — its snapshot is the real one, and the 0 is ours.
                    if (stored.navStatus !== 'missing') {
                        snapshot[item.id] = { quantity: stored.quantity, navStatus: stored.navStatus };
                    } else if (stored.quantity > 0) {
                        // Unless it is no longer 0: the job page knows nothing of `navStatus`, so
                        // that count is the office's edit mid-run (#107). It replaces the snapshot's
                        // count; the item's own `navStatus` stays as recorded. A new entry gets none
                        // — copying 'missing' in would bring it back still missing on un-mark.
                        snapshot[item.id] = { ...snapshot[item.id], quantity: stored.quantity };
                    }
                    this.store.dispatch(InventoryActions.itemEdited({
                        roomId: room.id,
                        item: { ...stored, quantity: 0, navStatus: 'missing' },
                    }));
                    continue;
                }

                // No longer marked missing, but the inventory still says so: an earlier confirm
                // zeroed it and nothing gave it back (#97).
                if (stored.navStatus === 'missing') {
                    this._dispatchRestored(room.id, stored, snapshot[item.id]);
                }
                delete snapshot[item.id];
            }
        }

        this.scopeConfirmed = true;
        this._review = { ...this._review, scopeConfirmed: true, missingSnapshot: snapshot };

        // The marks go in the confirm's own write, not `_flushReview`'s: a missing item's zero and
        // its snapshot must land together or not at all (#97, #86c). A copy, as in `_flushReview`.
        if (this._reviewTimer !== null) {
            clearTimeout(this._reviewTimer);
            this._reviewTimer = null;
        }
        if (this._eventId) {
            this.store.dispatch(quickJsActions.navigatorScopeConfirmButtonClicked({
                eventId: this._eventId,
                inventoryReview: structuredClone(this._review) as unknown as Record<string, unknown>,
            }));
        }

        this._notifyGate();
    }

    /**
     * Re-opens a confirmed scope so the crew can change the list they came back for (**#65**).
     *
     * **Room approvals are deliberately left intact.** Every control in this editor is already
     * gated on `scopeConfirmed`, so clearing it alone restores the whole editor — per-room "Undo"
     * included — and the crew can un-approve anything by hand. More importantly, editing an item
     * inside an approved room *already* revokes that room's approval on its own: `_setQty`,
     * `addItem` and `_revokeApprovalFor` each call `reopenRoom()`. So rooms the crew actually
     * touches lose their approval automatically, and rooms they do not keep it.
     *
     * That is why this does not reset every room. Doing so would force a full re-walk of a house
     * to change one item, and it would contradict the per-room behaviour that **#32** established
     * — approving a room must stay a deliberate act, distinct from ticking its last item.
     *
     * **Missing items get their quantity back** (**#97**), so the crew edits the real list rather
     * than a list of zeros, and an item they un-mark is no longer excluded from the totals. Items
     * still marked missing show their original count too; the next confirm zeroes them again.
     * The snapshot is kept, not cleared — see `missingSnapshot`.
     *
     * The gate is re-notified because `isSatisfied` is `scopeConfirmed`: without this the step
     * would stay passable while its own condition had become false.
     */
    unconfirmScope() {
        if (!this.scopeConfirmed) { return; }

        const storedRooms = this.store.selectSignal(jobInventoryFeature.selectInventoryEditor)().rooms ?? [];
        for (const room of storedRooms) {
            for (const stored of room.inventory ?? []) {
                const snap = this._review.missingSnapshot[stored.id];
                if (snap && stored.navStatus === 'missing') {
                    this._dispatchRestored(room.id, stored, snap);
                }
            }
        }

        this.scopeConfirmed = false;
        this._review = { ...this._review, scopeConfirmed: false };
        this._flushReview();

        this._notifyGate();
    }

    /**
     * Gives a zeroed missing item back its quantity and its own `navStatus`.
     *
     * Without a snapshot — a scope confirmed before #97 shipped — the original count is gone, so
     * the item comes back as 1 for the crew to correct. An item silently left
     * out of every total is worse than one with the wrong count on screen. A count the crew has
     * already set since takes precedence over both.
     */
    private _dispatchRestored(roomId: string, stored: InventoryItem, snap: MissingSnapshot | undefined) {
        const { navStatus: _missing, ...rest } = stored;
        const quantity = stored.quantity > 0 ? stored.quantity : (snap?.quantity ?? 1);
        this.store.dispatch(InventoryActions.itemEdited({
            roomId,
            item: snap?.navStatus ? { ...rest, quantity, navStatus: snap.navStatus } : { ...rest, quantity },
        }));
    }
}
